// src/components/ChatWidget.jsx
import React, { useEffect, useRef, useState } from "react";

/**
 * ChatWidget: a lightweight concierge-style bot (no backend required)
 * - Quick replies + scoping wizard (name, region, services, budget, timeline)
 * - Remembers returning visitors (localStorage)
 * - Recommends the best RMC/VP based on region
 * - One-click "Send to Venturly" via your Apps Script
 * - Optional mailto escalation to a human
 *
 * Additions in this version:
 * - Prominent CTA bar: “I’m ready to go Venturly” → jumps to #intake-form
 * - Secondary CTA: “Plan my project in chat”
 * - Intake-jump chips & phrase handling
 * - Header close (X), click-outside & Esc to close, 24h suppression after manual close
 */
const DISMISS_KEY = "vg_widget_last_dismissed";
const ONE_DAY = 24 * 60 * 60 * 1000;
const INTAKE_SELECTOR = "#intake-form";

export default function ChatWidget({
  APPS_SCRIPT_URL,
  directory = [],                 // [{ name, region, headshot }]
  position = "right-center",
  autoOpenOnce = true,
  delayMs = 2500,
}) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => [
    {
      id: "hello",
      from: "bot",
      text:
        "Hi! I’m Venti, your project concierge. Want help scoping a project, finding the right regional lead, or ready to jump straight to our intake form?",
      chips: ["I’m ready to go Venturly", "Plan my project", "Find regional lead", "Talk to a human"],
    },
  ]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);

  // Simple wizard state
  const [stage, setStage] = useState("idle");
  const [ctx, setCtx] = useState(() => ({
    name: localStorage.getItem("vg_name") || "",
    email: localStorage.getItem("vg_email") || "",
    region: "",
    services: [],
    budget: "",
    timeline: "",
  }));

  const viewRef = useRef(null);
  const panelRef = useRef(null);
  const launcherRef = useRef(null);

  // Auto-open once after delay (polite) unless dismissed in last 24h
  useEffect(() => {
    if (!autoOpenOnce) return;

    const lastDismiss = Number(localStorage.getItem(DISMISS_KEY) || 0);
    const recentlyDismissed = Date.now() - lastDismiss < ONE_DAY;
    if (recentlyDismissed) return;

    const seen = sessionStorage.getItem("vg_widget_seen");
    if (!seen) {
      const t = setTimeout(() => {
        setOpen(true);
        sessionStorage.setItem("vg_widget_seen", "1");
      }, delayMs);
      return () => clearTimeout(t);
    }
  }, [autoOpenOnce, delayMs]);

  // Auto-scroll
  useEffect(() => {
    if (viewRef.current) {
      viewRef.current.scrollTop = viewRef.current.scrollHeight;
    }
  }, [messages, typing, open]);

  // Esc to close
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && open) {
        handleClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Click-outside to close
  useEffect(() => {
    const onDown = (e) => {
      if (!open) return;
      const p = panelRef.current;
      const l = launcherRef.current;
      if (!p) return;
      const clickedInsidePanel = p.contains(e.target);
      const clickedLauncher = l && l.contains(e.target);
      if (!clickedInsidePanel && !clickedLauncher) {
        handleClose();
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  // Close helper (stores 24h suppression)
  const handleClose = () => {
    setOpen(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  };

  // Utilities
  const push = (m) => setMessages((x) => [...x, { id: cryptoRandom(), ...m }]);
  const cryptoRandom = () => Math.random().toString(36).slice(2);

  const setTypingThen = (fn, delay = 550) => {
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      fn?.();
    }, delay);
  };

  const startWizard = () => {
    setStage("name");
    push({
      from: "bot",
      text:
        ctx.name
          ? `Welcome back, ${ctx.name}! Want me to draft a quick brief for the team?`
          : "Great — let's draft a quick brief together.",
      chips: ctx.name ? ["Yes, let’s do it", "Not now", "Go to intake form"] : ["Go to intake form"],
    });
    if (!ctx.name) {
      setTypingThen(() =>
        push({ from: "bot", text: "First, what's your name?" })
      );
    }
  };

  const recommendRMC = (region) => {
    if (!region || !directory?.length) return null;
    const lower = region.toLowerCase();
    const match =
      directory.find((d) => d.region?.toLowerCase().includes(lower)) ||
      directory.find((d) => lower.includes(d.region?.toLowerCase()));
    return match || null;
  };

  const humanMailto = (summary) => {
    const subject = encodeURIComponent("Venturly — Project Intro");
    const body = encodeURIComponent(summary || "");
    return `mailto:info@venturlyglobal.com?subject=${subject}&body=${body}`;
  };

  const sendToAppsScript = async (payloadObj) => {
    if (!APPS_SCRIPT_URL) return;
    try {
      const payload = new URLSearchParams(payloadObj);
      const q = new URLSearchParams(window.location.search);
      ["utm_source","utm_medium","utm_campaign","utm_content","utm_region","utm_owner"].forEach((k) => {
        const v = q.get(k) || sessionStorage.getItem(k);
        if (v) payload.append(k, v);
      });
      payload.append("referrer", document.referrer || "");
      payload.append("path", window.location.pathname || "/");
      payload.append("source", "venturly-chat");

      await fetch(APPS_SCRIPT_URL, {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8" },
        body: payload.toString(),
      });
      return true;
    } catch (e) {
      console.error("Apps Script error", e);
      return false;
    }
  };

  /** ---------- Intake jump helpers ---------- */
  const jumpToIntake = () => {
    const el = document.querySelector(INTAKE_SELECTOR);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      // Try to focus first field after a short delay
      setTimeout(() => {
        const firstInput = el.querySelector("input, select, textarea");
        firstInput?.focus?.();
      }, 500);
      handleClose();
      return true;
    }
    return false;
  };

  /** ---------- Intent routing ---------- */

  const onChip = (label) => {
    userSays(label);
    handleText(label);
  };

  const userSays = (text) => {
    push({ from: "user", text });
  };

  const isIntakeIntent = (t) => {
    const s = t.toLowerCase();
    return (
      s.includes("ready to go") ||
      s.includes("go venturly") ||
      s.includes("intake") ||
      s.includes("get started") ||
      s.includes("start now")
    );
  };

  const handleText = (raw) => {
    const text = (raw || "").trim();

    // Fast path: jump to intake form
    if (isIntakeIntent(text)) {
      const ok = jumpToIntake();
      setTypingThen(() =>
        push({
          from: "bot",
          text: ok
            ? "Taking you to the intake form…"
            : "Hmm, I couldn’t find the intake form on this page.",
        })
      );
      return;
    }

    const lower = text.toLowerCase();

    // Wizard progression
    if (
      stage.startsWith("scope:") ||
      stage === "name" || stage === "email" || stage === "region" ||
      stage === "services" || stage === "budget" || stage === "timeline" ||
      stage === "summary"
    ) {
      return advanceWizard(text);
    }

    // Entry points
    if (lower.includes("plan") || lower.includes("wizard") || lower.includes("brief")) {
      return startWizard();
    }
    if (lower.includes("regional lead") || lower.includes("rmc") || lower.includes("vp")) {
      setTypingThen(() =>
        push({
          from: "bot",
          text: "Which region are you targeting?",
          chips: REGION_CHIPS,
        })
      );
      setStage("region-only");
      return;
    }
    if (lower.includes("human") || lower.includes("talk to a human") || lower.includes("contact")) {
      setTypingThen(() =>
        push({
          from: "bot",
          text:
            "No problem. I can package up your details and email the team, or you can email us directly.",
          chips: ["Send my details", "Email the team", "Go to intake form"],
        })
      );
      setStage("human-escalate");
      return;
    }

    // Fallback: light Q&A
    setTypingThen(() =>
      push({
        from: "bot",
        text:
          "I can help scope a project, recommend an RMC, or connect you with a human. Or jump straight to the intake form.",
        chips: ["I’m ready to go Venturly", "Plan my project", "Find regional lead", "Talk to a human"],
      })
    );
  };

  const advanceWizard = (text) => {
    // region-only quick path (no full wizard)
    if (stage === "region-only") {
      const region = text;
      const m = recommendRMC(region);
      if (m) {
        setTypingThen(() =>
          push({
            from: "bot",
            html: RmcCard({ rmc: m, region }),
            chips: ["Email intro", "Send to Venturly", "Go to intake form", "Plan my project"],
          })
        );
        setCtx((c) => ({ ...c, region }));
        setStage("region-done");
        return;
      }
      setTypingThen(() =>
        push({
          from: "bot",
          text: "I didn't catch that region. Try one of these:",
          chips: REGION_CHIPS,
        })
      );
      return;
    }

    // human escalate
    if (stage === "human-escalate") {
      if (text.toLowerCase().includes("email the team")) {
        const sum = "Hi Venturly team—I'd like to talk to a human.";
        window.location.href = humanMailto(sum);
        push({ from: "bot", text: "Opening your email client…" });
        setStage("idle");
        return;
      } else if (text.toLowerCase().includes("send my details")) {
        setTypingThen(() =>
          push({ from: "bot", text: "Great—what’s your email so we can reply?" })
        );
        setStage("email");
        return;
      } else if (isIntakeIntent(text)) {
        jumpToIntake();
        push({ from: "bot", text: "Taking you to the intake form…" });
        setStage("idle");
        return;
      }
    }

    // Wizard steps
    if (stage === "name") {
      const name = text.replace(/(^hi|hello|hey)\s*/i, "").trim();
      localStorage.setItem("vg_name", name);
      setCtx((c) => ({ ...c, name }));
      setTypingThen(() => {
        push({ from: "bot", text: `Nice to meet you, ${name}. What's your email? (optional)` });
      });
      setStage("email");
      return;
    }

    if (stage === "email") {
      const looksEmail = /\S+@\S+\.\S+/.test(text);
      if (looksEmail) {
        localStorage.setItem("vg_email", text);
        setCtx((c) => ({ ...c, email: text }));
      }
      setTypingThen(() =>
        push({
          from: "bot",
          text: "Which region is this for?",
          chips: REGION_CHIPS,
        })
      );
      setStage("region");
      return;
    }

    if (stage === "region") {
      setCtx((c) => ({ ...c, region: text }));
      setTypingThen(() =>
        push({
          from: "bot",
          text: "Which services do you need? (pick a few)",
          chips: SERVICE_CHIPS,
        })
      );
      setStage("services");
      return;
    }

    if (stage === "services") {
      const sels = Array.isArray(text) ? text : [text];
      setCtx((c) => ({ ...c, services: [...new Set([...(c.services || []), ...sels])] }));
      setTypingThen(() =>
        push({
          from: "bot",
          text: "Rough budget band?",
          chips: ["<$25k", "$25k–$100k", "$100k–$500k", ">$500k", "Not sure"],
        })
      );
      setStage("budget");
      return;
    }

    if (stage === "budget") {
      setCtx((c) => ({ ...c, budget: text }));
      setTypingThen(() =>
        push({
          from: "bot",
          text: "Timeline?",
          chips: ["ASAP (<4 weeks)", "1–3 months", "3–6 months", ">6 months"],
        })
      );
      setStage("timeline");
      return;
    }

    if (stage === "timeline") {
      const next = { ...ctx, timeline: text };
      setCtx(next);

      const rmc = recommendRMC(next.region);
      const summary =
        `Project brief
- Name: ${next.name || "—"}
- Email: ${next.email || "—"}
- Region: ${next.region || "—"}
- Services: ${next.services?.join(", ") || "—"}
- Budget: ${next.budget || "—"}
- Timeline: ${text || "—"}`;

      setTypingThen(() => {
        push({ from: "bot", text: "Here’s a quick summary. Want me to send this to the team?" });
        if (rmc) push({ from: "bot", html: RmcCard({ rmc, region: next.region }) });
        push({
          from: "bot",
          html: `<pre class="whitespace-pre-wrap bg-slate-50 p-3 rounded-lg ring-1 ring-slate-200 text-xs">${escapeHtml(summary)}</pre>`,
          chips: ["Send to Venturly", "Email intro", "Go to intake form", "Change region"],
        });
      });

      setStage("summary");
      return;
    }

    if (stage === "summary") {
      if (text.toLowerCase().includes("send to venturly")) {
        setTypingThen(async () => {
          const ok = await sendToAppsScript({
            name: ctx.name || "",
            email: ctx.email || "",
            description: `Chat Brief — ${ctx.services?.join(", ") || ""}`,
            region: ctx.region || "",
            budget: ctx.budget || "",
            timeline: ctx.timeline || "",
            chat_summary: JSON.stringify(ctx),
          });
          push({
            from: "bot",
            text: ok
              ? "Sent! A coordinator will get back to you shortly."
              : "Hmm, I couldn’t reach the server. You can email us directly or use the intake form.",
            chips: ok ? ["Go to intake form", "Start over", "Close"] : ["Go to intake form", "Email the team", "Close"],
          });
          setStage("idle");
        });
        return;
      }
      if (text.toLowerCase().includes("email intro")) {
        const sum =
          `Hi Venturly,\n\nPlease connect me with the regional lead.\n\n` +
          `Name: ${ctx.name || "—"}\nEmail: ${ctx.email || "—"}\nRegion: ${ctx.region || "—"}\n` +
          `Services: ${ctx.services?.join(", ") || "—"}\nBudget: ${ctx.budget || "—"}\nTimeline: ${ctx.timeline || "—"}\n\nThanks!`;
        window.location.href = humanMailto(sum);
        push({ from: "bot", text: "Opening your email client…" });
        setStage("idle");
        return;
      }
      if (isIntakeIntent(text)) {
        jumpToIntake();
        push({ from: "bot", text: "Taking you to the intake form…" });
        setStage("idle");
        return;
      }
      if (text.toLowerCase().includes("change region")) {
        setTypingThen(() =>
          push({ from: "bot", text: "Pick a region:", chips: REGION_CHIPS })
        );
        setStage("region");
        return;
      }
      if (text.toLowerCase().includes("start over") || text.toLowerCase() === "close") {
        setStage("idle");
        setTypingThen(() =>
          push({
            from: "bot",
            text: "What next?",
            chips: ["I’m ready to go Venturly", "Plan my project", "Find regional lead", "Talk to a human"],
          })
        );
        return;
      }
    }

    // Default safeguard
    setTypingThen(() =>
      push({
        from: "bot",
        text: "Got it. Want the quick project wizard, a regional match, a human, or jump straight to the intake form?",
        chips: ["I’m ready to go Venturly", "Plan my project", "Find regional lead", "Talk to a human"],
      })
    );
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    userSays(input);
    handleText(input);
    setInput("");
  };

  const posXY =
    position.includes("center")
      ? "bottom-24"
      : "bottom-5";

  return (
    <>
      {/* Launcher button (toggle) */}
      <button
        ref={launcherRef}
        onClick={() => setOpen((v) => !v)}
        className={`fixed z-[60] shadow-lg rounded-full px-4 py-2 text-white bg-slate-900 hover:bg-slate-800 ${
          position.includes("right") ? "right-5" : "left-5"
        } ${posXY}`}
        aria-expanded={open ? "true" : "false"}
      >
        {open ? "Close chat" : "Chat with Venturly"}
      </button>

      {/* Panel */}
      {open && (
        <div
          ref={panelRef}
          className={`fixed z-[60] w-96 max-w-[92vw] rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200 ${
            position.includes("right") ? "right-5" : "left-5"
          } ${posXY}`}
        >
          {/* Header with Close (X) */}
          <div className="flex items-center justify-between p-3 border-b border-slate-200">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-full bg-slate-900 text-white grid place-items-center text-xs">VG</div>
              <div>
                <div className="text-sm font-semibold text-slate-900">Venti</div>
                <div className="text-xs text-slate-600">Project concierge</div>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="text-slate-500 hover:text-slate-800 text-lg leading-none"
              aria-label="Close chat"
              title="Close"
            >
              ×
            </button>
          </div>

          {/* Transcript */}
          <div ref={viewRef} className="h-72 overflow-y-auto p-3 space-y-2">
            {messages.map((m) => (
              <Bubble key={m.id} m={m} onChip={onChip} />
            ))}
            {typing && (
              <div className="flex gap-2 items-center text-slate-500 text-xs pl-1">
                <span className="animate-pulse">Venti is typing…</span>
              </div>
            )}
          </div>

          {/* Sticky CTA bar (fast path to intake) */}
          <div className="p-3 border-t border-slate-200 bg-slate-50">
            <div className="flex flex-col gap-2">
              <button
                onClick={jumpToIntake}
                className="w-full px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"
              >
                I’m ready to go Venturly → Intake Form
              </button>
              <button
                onClick={startWizard}
                className="w-full px-4 py-2 rounded-lg text-sm font-medium bg-white ring-1 ring-slate-300 hover:bg-slate-50 text-slate-900"
              >
                Plan my project in chat
              </button>
            </div>
          </div>

          {/* Input */}
          <form onSubmit={onSubmit} className="p-3 border-t border-slate-200 flex gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
              placeholder="Type a message…  (Tip: type “intake” to jump)"
            />
            <button
              className="px-3 py-2 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-800"
              type="submit"
            >
              Send
            </button>
          </form>
        </div>
      )}
    </>
  );
}

/* ---------- UI bits ---------- */

function Bubble({ m, onChip }) {
  const isUser = m.from === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[82%] rounded-2xl px-3 py-2 text-sm ${
          isUser
            ? "bg-slate-900 text-white rounded-br-sm"
            : "bg-slate-100 text-slate-900 rounded-bl-sm"
        }`}
        dangerouslySetInnerHTML={m.html ? { __html: m.html } : undefined}
      >
        {!m.html && <span>{m.text}</span>}
      </div>
      {!isUser && m.chips?.length ? (
        <div className="basis-full mt-2 flex flex-wrap gap-2">
          {m.chips.map((c, i) => (
            <button
              key={i}
              onClick={() => onChip(c)}
              className="text-[11px] font-medium bg-white text-slate-800 px-3 py-1 rounded-full ring-1 ring-slate-200 hover:bg-slate-50"
            >
              {c}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/* ---------- Constants & helpers ---------- */

const REGION_CHIPS = [
  "West & Central Africa",
  "East & Southern Africa",
  "MENA",
  "South Asia",
  "Southeast Asia & Pacific",
  "Central Asia",
  "Eastern Europe & Eurasia",
  "Latin America & Caribbean",
  "Not sure",
];

const SERVICE_CHIPS = [
  "Policy reform",
  "Market entry / access",
  "Ops setup & logistics",
  "Research / MEL / TPM",
  "Education & workforce",
  "Health & social protection",
  "Infrastructure & energy",
  "Compliance & QA/QC",
  "Stakeholder engagement",
];

function RmcCard({ rmc, region }) {
  const img = rmc?.headshot || "/profiles/avatar_01.png";
  const name = rmc?.name || "Regional Lead";
  const reg = region || rmc?.region || "";
  return `
    <div class="flex items-center gap-3 p-2 rounded-xl ring-1 ring-slate-200 bg-white">
      <img src="${img}" alt="${name}" class="h-10 w-10 rounded-full object-cover ring-1 ring-slate-200" onerror="this.src='/profiles/avatar_01.png'"/>
      <div class="min-w-0">
        <div class="text-sm font-semibold text-slate-900">${escapeHtml(name)}</div>
        <div class="text-xs text-slate-600 truncate">${escapeHtml(reg)}</div>
      </div>
    </div>
  `;
}

function escapeHtml(str = "") {
  return String(str)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}
